// Tiny synthesized SFX — no audio files, no libraries. Pattern lifted from
// learningRev/learningRevDiscordBot/web/index.html: a `ping` primitive
// (oscillator + gain envelope, optional pitch glide) plus a `noiseBurst`
// (BufferSource of random samples through a BiquadFilter) for hisses and
// whooshes. AudioContext is created lazily and unlocked by the user-gesture
// from the autoplay-gate click in main.ts.

import type { VehicleId } from "./vehicles";

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
const lastPlayedAt: Map<string, number> = new Map();

function audioCtx(): AudioContext | null {
  if (!ctx) {
    try {
      const Ctor: typeof AudioContext | undefined =
        window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return null;
      ctx = new Ctor();
      master = ctx.createGain();
      master.gain.value = 0.55;
      master.connect(ctx.destination);
    } catch {
      return null;
    }
  }
  if (ctx.state === "suspended") ctx.resume().catch(() => {});
  return ctx;
}

interface PingOpts {
  freq:      number;
  duration:  number;
  type?:     OscillatorType;
  volume?:   number;
  attack?:   number;
  decay?:    number;        // target frequency to glide to over `duration`
  delay?:    number;        // seconds from now
}

function ping(o: PingOpts) {
  const c = audioCtx();
  if (!c || !master) return;
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = o.type ?? "sine";
  osc.frequency.value = o.freq;
  const start = c.currentTime + (o.delay ?? 0);
  const end = start + o.duration;
  const vol = o.volume ?? 0.06;
  gain.gain.setValueAtTime(0.0001, start);
  gain.gain.exponentialRampToValueAtTime(Math.max(vol, 0.0002), start + (o.attack ?? 0.006));
  gain.gain.exponentialRampToValueAtTime(0.0001, end);
  if (o.decay != null) osc.frequency.exponentialRampToValueAtTime(o.decay, end);
  osc.connect(gain).connect(master);
  osc.start(start);
  osc.stop(end + 0.02);
}

interface NoiseOpts {
  duration:    number;
  volume?:     number;
  type?:       BiquadFilterType;
  cutoff?:     number;
  cutoffEnd?:  number;
  delay?:      number;
}

function noiseBurst(o: NoiseOpts) {
  const c = audioCtx();
  if (!c || !master) return;
  const len = Math.floor(c.sampleRate * o.duration);
  const buf = c.createBuffer(1, len, c.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
  const src = c.createBufferSource();
  src.buffer = buf;
  const filter = c.createBiquadFilter();
  filter.type = o.type ?? "highpass";
  filter.frequency.value = o.cutoff ?? 800;
  const gain = c.createGain();
  const start = c.currentTime + (o.delay ?? 0);
  const end = start + o.duration;
  const vol = o.volume ?? 0.04;
  gain.gain.setValueAtTime(0.0001, start);
  gain.gain.exponentialRampToValueAtTime(Math.max(vol, 0.0002), start + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.0001, end);
  if (o.cutoffEnd != null) filter.frequency.exponentialRampToValueAtTime(o.cutoffEnd, end);
  src.connect(filter).connect(gain).connect(master);
  src.start(start);
  src.stop(end + 0.05);
}

// Suppress repeat triggers of the same recipe within `cooldownMs`. Without
// this, ten taps of the same card stack ten overlapping sirens.
function gate(key: string, cooldownMs: number): boolean {
  const now = performance.now();
  const last = lastPlayedAt.get(key) ?? 0;
  if (now - last < cooldownMs) return false;
  lastPlayedAt.set(key, now);
  return true;
}

// ---------------------------------------------------------------------------
// Semantic SFX
// ---------------------------------------------------------------------------

export const sfx = {
  // Call from a user-gesture handler so the AudioContext can leave the
  // suspended state. Harmless to call multiple times.
  unlock() { audioCtx(); },

  vehicle(id: VehicleId) {
    if (!gate(`v:${id}`, 220)) return;
    switch (id) {
      case "fire_truck":
        for (let i = 0; i < 3; i++) {
          ping({ freq: 740,  duration: 0.16, type: "square", volume: 0.05, delay: i * 0.34 });
          ping({ freq: 1080, duration: 0.16, type: "square", volume: 0.05, delay: i * 0.34 + 0.17 });
        }
        return;
      case "ambulance":
        for (let i = 0; i < 3; i++) {
          ping({ freq: 760, duration: 0.18, type: "sine", volume: 0.06, delay: i * 0.38 });
          ping({ freq: 980, duration: 0.18, type: "sine", volume: 0.06, delay: i * 0.38 + 0.19 });
        }
        return;
      case "police":
        for (let i = 0; i < 2; i++) {
          ping({ freq: 480, duration: 0.36, type: "sawtooth", volume: 0.045, decay: 1100, delay: i * 0.42 });
        }
        return;
      case "sports_car":
        ping({ freq: 140, duration: 0.55, type: "sawtooth", volume: 0.07, decay: 380 });
        return;
      case "crane":
        ping({ freq: 220, duration: 0.06, type: "square", volume: 0.06 });
        ping({ freq: 180, duration: 0.10, type: "square", volume: 0.05, delay: 0.07 });
        return;
      case "tractor":
        for (let i = 0; i < 3; i++) {
          ping({ freq: 90, duration: 0.10, type: "sawtooth", volume: 0.06, delay: i * 0.16 });
        }
        return;
      case "water_sprinkler":
        noiseBurst({ duration: 0.4, volume: 0.04, cutoff: 1200, cutoffEnd: 3000 });
        return;
      // generic truck/digger honk
      case "excavator":
      case "bulldozer":
      case "flatbed":
      case "mixer":
      case "road_roller":
      case "big_truck":
      default:
        ping({ freq: 200, duration: 0.18, type: "square", volume: 0.06 });
        ping({ freq: 160, duration: 0.22, type: "square", volume: 0.05, delay: 0.20 });
        return;
    }
  },

  // Short hiss per droplet, gated so it doesn't become a continuous wall.
  waterSpray() {
    if (!gate("water", 80)) return;
    noiseBurst({ duration: 0.22, volume: 0.03, cutoff: 1500, cutoffEnd: 2400 });
  },

  // Bell-like ascending chime when an injured person is rescued.
  rescueDing() {
    ping({ freq: 1320, duration: 0.18, type: "triangle", volume: 0.06 });
    ping({ freq: 1760, duration: 0.28, type: "triangle", volume: 0.05, delay: 0.10, decay: 1320 });
  },
};
